import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { agentMeta, VERDICT, SEVERITY } from "@/sim/lib";
import { cn } from "@/lib/utils";

function PersonaCard({ agent, index }) {
  const [open, setOpen] = useState(false);
  const meta = agentMeta(agent.agent_id);
  const Icon = meta.icon;
  const v = VERDICT[agent.verdict] || VERDICT.HESITANT;
  return (
    <motion.div
      data-testid={`sim-persona-${agent.agent_id}`}
      initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: index * 0.04 }}
      className={cn("sim-card rounded-lg overflow-hidden ring-1 ring-inset", v.ring)}
    >
      <button onClick={() => setOpen((o) => !o)} className="w-full p-3 flex items-center gap-3 text-left">
        <div className="w-9 h-9 rounded-md flex items-center justify-center shrink-0" style={{ background: `${meta.accent}14`, border: `1px solid ${meta.accent}33` }}>
          <Icon className="w-4 h-4" style={{ color: meta.accent }} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[#F5F5F5] text-[13px] font-semibold truncate">{agent.name}</p>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className={cn("w-1.5 h-1.5 rounded-full", v.dot)} />
            <span className={cn("sim-mono text-[10px]", v.text)}>{v.label}</span>
          </div>
        </div>
        <div className="text-right">
          <span className="sim-mono text-lg font-bold text-[#F5F5F5]">{agent.score}</span>
        </div>
        <ChevronDown className={cn("w-4 h-4 text-[#9AA0AC] transition-transform", open && "rotate-180")} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="px-3 pb-3 pt-1 border-t border-[#27272A]">
              <p className="text-[#D4D4D8] text-xs italic leading-relaxed mb-2">“{agent.summary}”</p>
              {(agent.objections || []).map((o, i) => {
                const s = SEVERITY[o.severity] || SEVERITY.MEDIUM;
                return (
                  <div key={i} className="mb-1.5">
                    <span className={cn("sim-mono text-[9px] px-1.5 py-0.5 rounded border mr-1.5", s.chip)}>{s.label}</span>
                    <span className="text-[#D4D4D8] text-[11px]">{o.issue}</span>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export function PersonaScorecard({ agents = [] }) {
  return (
    <div data-testid="sim-scorecard">
      <p className="sim-mono text-[11px] text-[#D4D4D8] uppercase tracking-wider mb-3">Persona Scorecard · {agents.length}/9 agents</p>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2.5">
        {agents.map((a, i) => <PersonaCard key={a.agent_id} agent={a} index={i} />)}
      </div>
    </div>
  );
}
